import { useCallback, useEffect, useRef, useState } from 'react';

import { SETTINGS_DEVICES_CONFIGURATION_LAYOUT_SECTIONS } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-layout/constants';
import { DeviceLayoutConfigSectionDetails } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-layout/settings-devices-configuration-layout.model';
import { useConfigurationDetailContext } from './use-configuration-detail-context.hook';
import { requestSectionDetails } from '../layout-section-request.store';

export const useDeviceConfigurationLayout = () => {
    const {
        layoutSectionSelected,
        layoutSections,
        sectionDetails,
        setSectionDetails,
        setLayoutSectionSelected,
        isLoading,
        setIsLoading,
    } = useConfigurationDetailContext();

    const storedSectionRef = useRef<{
        [id: string]: DeviceLayoutConfigSectionDetails;
    }>({});
    const activeSectionRequestRef = useRef(0);
    const [sectionLoadError, setSectionLoadError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const retrySectionDetailsLoad = useCallback(() => {
        setRetryCount((current) => current + 1);
    }, []);

    useEffect(() => {
        if (!(layoutSectionSelected && layoutSections?.id)) {
            return;
        }

        if (storedSectionRef.current[layoutSectionSelected]) {
            setSectionDetails(storedSectionRef.current[layoutSectionSelected]);
            setSectionLoadError(false);
            setIsLoading(false);
            return;
        }

        const requestId = ++activeSectionRequestRef.current;
        const isCurrentRequest = () => activeSectionRequestRef.current === requestId;
        const runIfCurrentRequest = (callback: VoidFunction) => {
            if (!isCurrentRequest()) {
                return;
            }

            callback();
        };

        setSectionDetails(undefined);
        setSectionLoadError(false);
        setIsLoading(true);

        const inFlightRequest = requestSectionDetails(layoutSections.id, layoutSectionSelected);

        inFlightRequest
            .then((result) => {
                runIfCurrentRequest(() => {
                    storedSectionRef.current[layoutSectionSelected] = result;
                    setSectionDetails(result);
                });
            })
            .catch(() => {
                runIfCurrentRequest(() => {
                    setSectionLoadError(true);
                    setSectionDetails(undefined);
                });
            })
            .finally(() => {
                runIfCurrentRequest(() => {
                    setIsLoading(false);
                });
            });
    }, [layoutSectionSelected, layoutSections?.id, retryCount, setIsLoading, setSectionDetails]);

    const selectedSection = layoutSections?.sections.find((layout) => layoutSectionSelected === layout.id);

    const sectionTitle = selectedSection?.displayName;
    const sectionName = selectedSection?.name;

    const isFlexEnd = sectionName === SETTINGS_DEVICES_CONFIGURATION_LAYOUT_SECTIONS.EVENT_HANDLING;

    return {
        isLoading,
        layoutSections,
        layoutSectionSelected,
        sectionDetails,
        sectionLoadError,
        sectionTitle,
        isFlexEnd,
        retrySectionDetailsLoad,
        setLayoutSectionSelected,
    };
};
